import { useEffect, useState } from "react";
import { ArrowRight, CheckCircle2, Clock3, Loader2, Search, ShieldAlert, Vote } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { apiUrl, webSocketUrl } from "@/lib/api-url";

type VoterStatus = {
  convention: { name: string };
  session: { title: string; status: string; primaryOffice: string; optionalOffice: string };
  voter: { delegateName: string };
  token: { status: string };
  permissions: { canVote: boolean; canViewBallot: boolean };
  message: string;
};

type Ballot = {
  candidates: Array<{ id: string; fullName: string }>;
};

type PublicResult = {
  status: "elected" | "no_majority" | "tie" | "cancelled";
  message?: string;
  primaryOffice?: string;
  primaryWinner?: { fullName: string; votes: number } | null;
  optionalOffice?: string;
  optionalWinner?: { fullName: string; votes: number } | null;
};

type LoadState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; voterStatus: VoterStatus; ballot: Ballot | null; publicResult: PublicResult | null };

type RealtimeState = "connecting" | "connected" | "reconnecting" | "offline";
type VoterStep = "waiting" | "choose" | "confirm" | "received" | "result";

type VoterAccessPageProps = {
  conventionSlug: string | null;
  sessionCode: string;
  token: string;
};

const messageLabels: Record<string, string> = {
  token_revoked: "Ce lien de vote a ete revoque. Demandez un nouveau lien au comite.",
  token_expired: "Ce lien de vote a expire.",
  vote_already_received: "Votre vote a deja ete recu. Aucun choix n'est affiche apres soumission.",
  vote_open: "Le vote est ouvert. Choisissez une personne eligible puis confirmez.",
  waiting_for_vote_to_open: "Le vote n'est pas encore ouvert. Cette page se mettra a jour lorsque la session commencera.",
  vote_closed: "Cette session de vote est cloturee.",
  vote_unavailable: "Cette session n'est pas disponible."
};

export function VoterAccessPage({ conventionSlug, sessionCode, token }: VoterAccessPageProps) {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [query, setQuery] = useState("");
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [submissionState, setSubmissionState] = useState<"idle" | "submitting" | "submitted" | "error">("idle");
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [realtimeState, setRealtimeState] = useState<RealtimeState>("connecting");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setState({ status: "loading" });
      try {
        const statusResponse = await fetch(apiUrl(voteApiPath(conventionSlug, sessionCode, token, "status")), {
          headers: { Accept: "application/json" }
        });

        if (!statusResponse.ok) {
          throw new Error(await voterErrorMessage(statusResponse, statusResponse.status === 401 ? "Lien de vote invalide." : "Impossible de verifier ce lien."));
        }

        const statusPayload = (await statusResponse.json()) as { status: VoterStatus };
        let ballot: Ballot | null = null;
        let publicResult: PublicResult | null = null;

        if (statusPayload.status.permissions.canViewBallot) {
          const ballotResponse = await fetch(apiUrl(voteApiPath(conventionSlug, sessionCode, token, "ballot")), {
            headers: { Accept: "application/json" }
          });

          if (!ballotResponse.ok) {
            throw new Error(await voterErrorMessage(ballotResponse, "Impossible de charger le bulletin."));
          }

          const ballotPayload = (await ballotResponse.json()) as { ballot: Ballot };
          ballot = ballotPayload.ballot;
        }

        if (["closed", "archived", "cancelled"].includes(statusPayload.status.session.status) || statusPayload.status.token.status === "spent") {
          const resultResponse = await fetch(apiUrl(voteApiPath(conventionSlug, sessionCode, token, "public-result")), {
            headers: { Accept: "application/json" }
          });

          if (resultResponse.ok) {
            const resultPayload = (await resultResponse.json()) as { result: PublicResult };
            publicResult = resultPayload.result;
          }
        }

        if (!cancelled) {
          setState({ status: "ready", voterStatus: statusPayload.status, ballot, publicResult });
        }
      } catch (error) {
        if (!cancelled) {
          setState({ status: "error", message: error instanceof Error ? error.message : "Erreur inconnue." });
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [conventionSlug, sessionCode, token, reloadNonce]);

  useEffect(() => {
    let closed = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: number | null = null;
    let attempt = 0;

    function connect() {
      setRealtimeState(attempt === 0 ? "connecting" : "reconnecting");
      socket = new WebSocket(webSocketUrl(`/api/realtime/vote/${encodeURIComponent(sessionCode)}?token=${encodeURIComponent(token)}`));

      socket.onopen = () => {
        attempt = 0;
        setRealtimeState("connected");
      };

      socket.onmessage = (event) => {
        if (shouldReloadForRealtimeMessage(event.data)) {
          setReloadNonce((current) => current + 1);
        }
      };

      socket.onclose = () => {
        if (closed) {
          return;
        }

        attempt += 1;
        setRealtimeState("reconnecting");
        reconnectTimer = window.setTimeout(connect, Math.min(1000 * 2 ** attempt, 10000));
      };

      socket.onerror = () => {
        setRealtimeState("offline");
        socket?.close();
      };
    }

    connect();
    return () => {
      closed = true;
      if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
      }
      socket?.close();
    };
  }, [sessionCode, token]);

  if (state.status === "loading") {
    return <VoterShell title="Verification du lien" description="Nous verifions votre session de vote.">
      <Card className="bg-card/90">
        <CardContent role="status" aria-live="polite" aria-busy="true" className="flex items-center gap-3 p-6 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" aria-hidden="true" />
          Chargement du bulletin...
        </CardContent>
      </Card>
    </VoterShell>;
  }

  if (state.status === "error") {
    return <VoterShell title="Lien non valide" description="Le vote ne peut pas etre ouvert avec ce lien.">
      <Alert role="alert" className="border-destructive/30 bg-card/90">
        <ShieldAlert className="absolute left-4 top-4 size-4 text-destructive" aria-hidden="true" />
        <div className="space-y-3 pl-7">
          <AlertTitle>Acces refuse</AlertTitle>
          <AlertDescription>{state.message}</AlertDescription>
          <Button type="button" variant="outline" onClick={() => window.location.reload()}>
            Reessayer
          </Button>
        </div>
      </Alert>
    </VoterShell>;
  }

  const { voterStatus, ballot, publicResult } = state;
  const candidates = ballot?.candidates ?? [];
  const normalizedQuery = normalizeSearchValue(query);
  const filteredCandidates = candidates.filter((candidate) =>
    normalizeSearchValue(candidate.fullName).includes(normalizedQuery)
  );
  const selectedCandidate = candidates.find((candidate) => candidate.id === selectedCandidateId) ?? null;
  const currentStep = voterCurrentStep(voterStatus, ballot, publicResult, submissionState, isConfirming, selectedCandidateId);

  async function submitSelection() {
    if (!selectedCandidate) {
      return;
    }

    setSubmissionState("submitting");
    setSubmissionError(null);

    try {
      const response = await fetch(apiUrl(voteApiPath(conventionSlug, sessionCode, token, "submit")), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          candidateDelegateId: selectedCandidate.id,
          clientSubmissionId: crypto.randomUUID()
        })
      });

      if (!response.ok) {
        throw new Error(await voterErrorMessage(response, "Le vote n'a pas pu etre enregistre. Veuillez reessayer ou contacter le comite."));
      }

      setSelectedCandidateId(null);
      setIsConfirming(false);
      setSubmissionState("submitted");
      setReloadNonce((current) => current + 1);
    } catch (error) {
      setSubmissionState("error");
      setSubmissionError(error instanceof Error ? error.message : "Erreur inconnue.");
    }
  }

  return (
    <VoterShell title={voterStatus.session.title} description={voterStatus.convention.name}>
      <RealtimeStatus state={realtimeState} />
      <VoterProgress currentStep={currentStep} />
      <Card className="bg-card/90">
        <CardHeader>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">{officeLabel(voterStatus.session.primaryOffice)}</Badge>
            <Badge variant="outline">{sessionStatusLabel(voterStatus.session.status)}</Badge>
          </div>
          <CardTitle>{voterStepTitle(currentStep)}</CardTitle>
          <CardDescription>
            {voterStatus.voter.delegateName}, {messageLabels[voterStatus.message] ?? "statut du vote mis a jour."}
          </CardDescription>
        </CardHeader>
      </Card>

      {voterStatus.token.status === "spent" ? (
        <Alert className="border-primary/20 bg-card/90">
          <CheckCircle2 className="absolute left-4 top-4 size-4 text-primary" aria-hidden="true" />
          <div className="pl-7">
            <AlertTitle>Vote deja recu</AlertTitle>
            <AlertDescription>Le systeme ne reaffiche jamais le choix effectue.</AlertDescription>
          </div>
        </Alert>
      ) : null}

      {submissionState === "submitted" && voterStatus.token.status !== "spent" ? (
        <Alert className="border-primary/20 bg-card/90">
          <CheckCircle2 className="absolute left-4 top-4 size-4 text-primary" aria-hidden="true" />
          <div className="pl-7">
            <AlertTitle>Vote recu</AlertTitle>
            <AlertDescription>
              Votre vote a ete enregistre. Pour proteger la confidentialite, votre choix n'est plus affiche.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      {publicResult ? <PublicResultCard result={publicResult} /> : null}

      {!ballot && !publicResult && voterStatus.token.status !== "spent" ? (
        <WaitingRoomCard message={messageLabels[voterStatus.message] ?? "La page se mettra a jour automatiquement."} onRefresh={() => setReloadNonce((current) => current + 1)} />
      ) : null}

      {ballot && submissionState !== "submitted" ? (
        <Card className="bg-card/90">
          <CardHeader>
            <CardTitle>{isConfirming ? "Verifier avant envoi" : "Choisir une personne"}</CardTitle>
            <CardDescription>Seules les personnes presentes lors de l'appel sont affichees. Un seul vote est possible.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="relative block">
              <Search className="pointer-events-none absolute left-3 top-3 size-4 text-muted-foreground" aria-hidden="true" />
              <span className="sr-only">Rechercher un candidat</span>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="h-11 w-full rounded-full border border-input bg-background/80 pl-10 pr-4 text-sm outline-none ring-offset-background transition focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Rechercher par nom"
              />
            </label>

            <div className="grid max-h-[44vh] gap-2 overflow-auto pr-1" role="listbox" aria-label="Candidats eligibles">
              {filteredCandidates.length === 0 ? (
                <p className="rounded-2xl border border-dashed border-border bg-background/60 px-4 py-6 text-center text-sm text-muted-foreground">
                  Aucun candidat ne correspond a cette recherche.
                </p>
              ) : null}
              {filteredCandidates.map((candidate) => (
                <button
                  key={candidate.id}
                  type="button"
                  role="option"
                  aria-selected={selectedCandidateId === candidate.id}
                  onClick={() => {
                    setSelectedCandidateId(candidate.id);
                    setIsConfirming(false);
                  }}
                  className={`rounded-2xl border px-4 py-3 text-left text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    selectedCandidateId === candidate.id
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-background/70 hover:bg-secondary"
                  }`}
                >
                  {candidate.fullName}
                </button>
              ))}
            </div>

            {selectedCandidate && !isConfirming ? (
              <div className="sticky bottom-3 rounded-3xl border border-primary/25 bg-card/95 p-3 shadow-xl shadow-foreground/10 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Selection actuelle</p>
                <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="font-semibold">{selectedCandidate.fullName}</p>
                  <Button className="w-full sm:w-auto" size="lg" onClick={() => setIsConfirming(true)}>
                    Verifier et confirmer
                    <ArrowRight aria-hidden="true" />
                  </Button>
                </div>
              </div>
            ) : null}

            {selectedCandidate && isConfirming ? (
              <Alert className="border-primary/20 bg-secondary/70">
                <Vote className="absolute left-4 top-4 size-4 text-primary" aria-hidden="true" />
                <div className="space-y-3 pl-7">
                  <AlertTitle>Confirmer avant soumission</AlertTitle>
                  <AlertDescription>
                    Vous avez selectionne <strong>{selectedCandidate.fullName}</strong>. Apres soumission, ce choix ne
                    sera plus affiche.
                  </AlertDescription>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button className="w-full sm:w-auto" onClick={submitSelection} disabled={submissionState === "submitting"}>
                      {submissionState === "submitting" ? "Enregistrement..." : "Soumettre mon vote"}
                    </Button>
                    <Button variant="outline" className="w-full sm:w-auto" onClick={() => setIsConfirming(false)}>
                      Modifier mon choix
                    </Button>
                  </div>
                  {submissionState === "error" && submissionError ? (
                    <Alert role="alert" className="border-destructive/30 bg-background/80">
                      <AlertTitle>Enregistrement impossible</AlertTitle>
                      <AlertDescription>{submissionError}</AlertDescription>
                    </Alert>
                  ) : null}
                </div>
              </Alert>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </VoterShell>
  );
}

function VoterProgress({ currentStep }: { currentStep: VoterStep }) {
  const steps: Array<{ id: VoterStep; label: string }> = [
    { id: "waiting", label: "Acces" },
    { id: "choose", label: "Choix" },
    { id: "confirm", label: "Confirmation" },
    { id: "received", label: "Recu" },
    { id: "result", label: "Resultat" }
  ];
  const currentIndex = Math.max(0, steps.findIndex((step) => step.id === currentStep));

  return (
    <Card className="bg-card/85">
      <CardContent className="grid gap-2 p-3 sm:grid-cols-5" aria-label="Progression du vote">
        {steps.map((step, index) => {
          const state = index < currentIndex ? "done" : index === currentIndex ? "current" : "locked";

          return (
            <div
              key={step.id}
              className={`rounded-2xl border px-3 py-2 text-center text-xs font-semibold ${
                state === "done"
                  ? "border-primary/30 bg-primary/10 text-primary"
                  : state === "current"
                    ? "border-accent bg-accent/25 text-foreground"
                    : "border-border bg-background/50 text-muted-foreground"
              }`}
            >
              {step.label}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function WaitingRoomCard({ message, onRefresh }: { message: string; onRefresh: () => void }) {
  return (
    <Card className="bg-card/90">
      <CardHeader>
        <div className="flex items-center gap-2 text-primary">
          <Clock3 className="size-5" aria-hidden="true" />
          <CardTitle>Salle d'attente</CardTitle>
        </div>
        <CardDescription>{message}</CardDescription>
      </CardHeader>
      <CardContent>
        <Button type="button" variant="outline" className="w-full" onClick={onRefresh}>
          Actualiser maintenant
        </Button>
      </CardContent>
    </Card>
  );
}

function PublicResultCard({ result }: { result: PublicResult }) {
  if (result.status === "tie") {
    return (
      <Alert className="border-accent/40 bg-card/90">
        <ShieldAlert className="absolute left-4 top-4 size-4 text-accent-foreground" aria-hidden="true" />
        <div className="pl-7">
          <AlertTitle>Ballotage</AlertTitle>
          <AlertDescription>Un nouveau tour est requis. Les details du ballotage ne sont pas affiches ici.</AlertDescription>
        </div>
      </Alert>
    );
  }

  if (result.status === "no_majority") {
    return (
      <Alert className="border-accent/40 bg-card/90">
        <ShieldAlert className="absolute left-4 top-4 size-4 text-accent-foreground" aria-hidden="true" />
        <div className="pl-7">
          <AlertTitle>Nouveau tour requis</AlertTitle>
          <AlertDescription>La majorite requise n'a pas ete atteinte. Les scores detailles ne sont pas affiches ici.</AlertDescription>
        </div>
      </Alert>
    );
  }

  if (result.status === "cancelled") {
    return (
      <Alert className="border-destructive/30 bg-card/90">
        <ShieldAlert className="absolute left-4 top-4 size-4 text-destructive" aria-hidden="true" />
        <div className="pl-7">
          <AlertTitle>Vote annule</AlertTitle>
          <AlertDescription>Cette session de vote a ete annulee.</AlertDescription>
        </div>
      </Alert>
    );
  }

  return (
    <Card className="bg-card/90">
      <CardHeader>
        <CardTitle>Resultat public</CardTitle>
        <CardDescription>Seuls les resultats finaux autorises sont affiches.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {result.primaryWinner ? (
          <ResultLine label={officeLabel(result.primaryOffice ?? "custom")} winner={result.primaryWinner} />
        ) : null}
        {result.optionalWinner && result.optionalOffice && result.optionalOffice !== "none" ? (
          <ResultLine label={officeLabel(result.optionalOffice)} winner={result.optionalWinner} />
        ) : null}
      </CardContent>
    </Card>
  );
}

function ResultLine({ label, winner }: { label: string; winner: { fullName: string; votes: number } }) {
  return (
    <div className="rounded-2xl bg-secondary/70 p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-semibold text-foreground">{winner.fullName}</p>
      <p className="text-sm text-muted-foreground">{winner.votes} voix</p>
    </div>
  );
}

function RealtimeStatus({ state }: { state: RealtimeState }) {
  if (state === "connected") {
    return null;
  }

  const label = state === "offline" ? "Connexion temps reel interrompue." : "Reconnexion temps reel en cours...";
  return (
    <Alert className="border-accent/40 bg-card/90">
      <Loader2 className="absolute left-4 top-4 size-4 animate-spin text-primary" aria-hidden="true" />
      <div className="pl-7">
        <AlertTitle>Synchronisation</AlertTitle>
        <AlertDescription>{label} La page recharge automatiquement les changements de session.</AlertDescription>
      </div>
    </Alert>
  );
}

function shouldReloadForRealtimeMessage(value: unknown) {
  if (typeof value !== "string") {
    return false;
  }

  try {
    const parsed = JSON.parse(value) as { type?: unknown };
    return ["session.state", "session.status_changed", "public_result.available"].includes(String(parsed.type));
  } catch {
    return false;
  }
}

function VoterShell({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top,oklch(0.86_0.1_83.2_/_0.25),transparent_34%),linear-gradient(180deg,oklch(0.985_0.018_96.8),oklch(0.92_0.035_157.8))] px-4 py-5 sm:px-6">
      <div className="mx-auto flex min-h-[calc(100vh-2.5rem)] w-full max-w-2xl flex-col gap-4">
        <header className="rounded-[2rem] border border-border/80 bg-card/90 p-5 shadow-xl shadow-foreground/10 backdrop-blur">
          <Badge variant="outline" className="mb-4 bg-background/70">Acces votant</Badge>
          <h1 className="font-serif text-3xl font-semibold leading-tight tracking-[-0.04em] sm:text-4xl">{title}</h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
        </header>
        {children}
      </div>
    </main>
  );
}

function voterCurrentStep(
  voterStatus: VoterStatus,
  ballot: Ballot | null,
  publicResult: PublicResult | null,
  submissionState: "idle" | "submitting" | "submitted" | "error",
  isConfirming: boolean,
  selectedCandidateId: string | null
): VoterStep {
  if (publicResult) {
    return "result";
  }

  if (submissionState === "submitted" || voterStatus.token.status === "spent") {
    return "received";
  }

  if (ballot && isConfirming && selectedCandidateId) {
    return "confirm";
  }

  if (ballot) {
    return "choose";
  }

  return "waiting";
}

function voterStepTitle(step: VoterStep) {
  if (step === "result") return "Resultat disponible";
  if (step === "received") return "Vote recu";
  if (step === "confirm") return "Derniere verification";
  if (step === "choose") return "Bulletin ouvert";
  return "En attente d'ouverture";
}

function normalizeSearchValue(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function voteApiPath(conventionSlug: string | null, sessionCode: string, token: string, action: string) {
  const encodedSessionCode = encodeURIComponent(sessionCode);
  const encodedToken = encodeURIComponent(token);
  if (conventionSlug) {
    return `/api/vote/${encodeURIComponent(conventionSlug)}/${encodedSessionCode}/${encodedToken}/${action}`;
  }

  return `/api/vote/${encodedSessionCode}/${encodedToken}/${action}`;
}

async function voterErrorMessage(response: Response, fallback: string) {
  try {
    const payload = (await response.json()) as { error?: string };
    if (payload.error) {
      return voterErrorLabels[payload.error] ?? fallback;
    }
  } catch {
    // Keep the non-sensitive fallback message.
  }

  return fallback;
}

const voterErrorLabels: Record<string, string> = {
  candidate_not_eligible: "Cette personne n'est pas eligible dans le snapshot. Recherchez un autre nom ou contactez le comite.",
  eligibility_snapshot_required: "Le vote n'est pas encore pret. Attendez l'ouverture officielle.",
  invalid_token: "Lien de vote invalide. Demandez un nouveau QR code au comite.",
  public_result_not_available: "Les resultats publics ne sont pas encore disponibles.",
  rate_limited: "Trop de tentatives rapides. Patientez quelques instants puis reessayez.",
  token_expired: "Ce lien de vote a expire. Demandez un nouveau lien au comite.",
  token_revoked: "Ce lien de vote a ete revoque. Demandez un nouveau lien au comite.",
  token_spent: "Votre vote a deja ete recu. Aucun choix n'est reaffiche.",
  vote_not_open: "Le vote n'est pas ouvert. Cette page se mettra a jour automatiquement.",
  voter_not_eligible: "Ce lien n'est pas eligible pour cette session. Contactez le comite sans transmettre votre token."
};

function officeLabel(office: string) {
  if (office === "president") return "President";
  if (office === "secretary") return "Secretaire";
  if (office === "vice_president") return "Vice-president";
  if (office === "recording_secretary") return "Secretaire rapporteur";
  if (office === "vice_secretary") return "Vice-secretaire";
  return "Poste personnalise";
}

function sessionStatusLabel(status: string) {
  if (status === "open") return "Vote ouvert";
  if (status === "ready") return "En attente";
  if (status === "closed") return "Cloture";
  if (status === "cancelled") return "Annule";
  return "Preparation";
}
